import * as models from "app/models/requireme";
import { createSelectorRx, search, nullsafe, assertUnreachable, mapMany, exclude, excludeKeys, leftJoin, union, unionKey, first, intersect, coalesce, intersectKeys, sum, any, contains, innerJoin, firstMap } from "keautils";
import { GuiaEntrega, InfoEntrega, RecordatorioAcuse } from "./model";



/**Obtiene los acuses que el usuario debe de entregar al cliente para que los firme */
export function obtenerRecordatoriosAcuse(state: models.ViajeAppIniciado, claveEntrega: string): RecordatorioAcuse[] {
    const entrega = obtenerEntrega(state, claveEntrega);
    const todasGuias = state.datos.Guias;
    const idGuiasEntregadas = entrega.IdGuiasEntregadas;
    const guiasEntregadas = intersectKeys(todasGuias, idGuiasEntregadas, x => x.IdRegistro);
    const acuses = guiasEntregadas
        .filter(x => x.ServiciosAdicionales.TipoAcuse != null)
        .map<RecordatorioAcuse>(x => ({
            folio: x.Folio,
            idGuia: x.IdRegistro,
            requerimientoAcuse: x.ServiciosAdicionales.NombreRequerimientoAcuse,
            tipoAcuse: x.ServiciosAdicionales.TipoAcuse!
        }));

    return acuses;
}

export function obtenerEntrega(state: models.ViajeAppIniciado, claveEntrega: string): models.EntregaRealizadaViajeAppModel {
    //Obtemos esta entrega y las otras entregas de la misma direccion
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    return entrega;
}

/**Obtiene las guias que participan en cierta entrega */
export function obtenerGuiasEntrega(state: models.ViajeAppIniciado, claveEntrega: string): models.GuiaDTO[] {
    //Obtemos esta entrega y las otras entregas de la misma direccion
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    const idDireccion = entrega.IdDireccionDestino;
    const otrasEntregasMismaDireccion = state.model.EntregasRealizadas.filter(x => x.ClaveUnica != claveEntrega && x.IdDireccionDestino == idDireccion);

    //Obtenemos todas las guias que van para el destino de esa entrega
    const todasGuias = state.datos.Guias;
    const guiasDireccion = todasGuias.filter(x => x.IdDireccionClienteDestino == idDireccion);

    //Los ids de las guias que ya estan incluidas en otras entregas, estas guias se van a tener que excluir
    const IdGuiasOtrasEntregas = [
        ...mapMany(otrasEntregasMismaDireccion, x => x.GuiasNoEntregadas).map(x => x.IdGuia),
        ...mapMany(otrasEntregasMismaDireccion, x => x.IdGuiasEntregadas)
    ];

    //Las guias que se van a mostrar son todas las guias de ese destino, menos las guias que ya fueron incluidas en otras entregas de ese mismo destino
    const guias = excludeKeys(guiasDireccion, IdGuiasOtrasEntregas, x => x.IdRegistro);

    return guias;
}

/**Devuelve true si se puede finalizar una entrega, o una cadena explicando la razon de porque no se puede */
export function puedeFinalizar(state: models.ViajeAppIniciado, claveEntrega: string): true | "faltaMotivo" | "faltaContacto" | "saldoInsuficiente" | "creditoBloqueado" | "sinGuias" {
    //Obtemos esta entrega y las otras entregas de la misma direccion
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];

    if (entrega.IdContactoRecibio == null && (entrega.NombreContactoRecibio == null || entrega.NombreContactoRecibio == "" || entrega.NombreContactoRecibio.trim().length == 0)) return "faltaContacto";
    const faltaMotivo = any(entrega.GuiasNoEntregadas, x => !x.Motivo);
    if (faltaMotivo) return "faltaMotivo";

    //Solo verificamos el limite de credito si el tipo de pago es crédito, por que es posible que el cliente se pase del limite de crédito con otros pagos pero aún asi
    //nos debe de permitir pagar con efectivo o cheque
    if (entrega.TipoPago == models.TipoPagoEntregaDomicilio.Credito) {
        //Simulamos la finalización y vemos si el saldo del cliente supera al limite de crédito del cliente
        const finalizacionSimulada = onFinalizarEntrega(state, claveEntrega, { Segmentos: [] });
        const saldoSimulado = obtenerSaldoClienteDeEntrega(finalizacionSimulada, claveEntrega);

        if (saldoSimulado.saldo > saldoSimulado.limite)
            return "saldoInsuficiente";

        if (saldoSimulado.creditoBloqueado)
            return "creditoBloqueado";
    }

    const cantGuias = entrega.GuiasNoEntregadas.length + entrega.IdGuiasEntregadas.length;
    if (cantGuias == 0) return "sinGuias";

    return true;
}


/**Obtiene los contactos que puede recibir en el cliente */
export function obtenerContactos(state: models.ViajeAppIniciado, claveEntrega: string): models.ContactoCliente[] {
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    const idDireccion = entrega.IdDireccionDestino;
    const direccion = first(mapMany(state.datos.Clientes, x => x.Direcciones), x => x.IdRegistro == idDireccion)!;
    const idCliente = direccion.IdCliente;
    const SoloRecibirContactosAutorizado = firstMap(state.datos.Clientes, x => x.IdRegistro == idCliente, x => x.SoloRecibirContactosAutorizado)!;
    const contactos = direccion.Contactos.filter(x => !SoloRecibirContactosAutorizado || x.AutorizadoRecibir);
    return contactos;
}

/**Obtiene el total a pagar de una entrega, el campo "todas" es el total de todas las guias que se deben de entregar en esta entrega,
 * y el campo "entregadas" es el importe solo de las guias que si estan marcadas como entregadas
 */
export function obtenerTotalAPagar(state: models.ViajeAppIniciado, claveEntrega: string): {
    todas: number,
    entregadas: number
} {
    //Las guias que se van a mostrar son todas las guias de ese destino, menos las guias que ya fueron incluidas en otras entregas de ese mismo destino
    const guias = obtenerGuiasEntrega(state, claveEntrega);
    const guiasACobrar = guias.filter(x => x.FletePorCobrar);

    //Obtemos esta entrega y las otras entregas de la misma direccion
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    const idGuiasEntregadas = entrega.IdGuiasEntregadas;
    const guiasAPagar = intersectKeys(guiasACobrar, idGuiasEntregadas, x => x.IdRegistro);

    const importeEntregadas = sum(guiasAPagar.map(x => x.Importes ? x.Importes.Total : 0));
    const importeTodas = sum(guiasACobrar.map(x => x.Importes ? x.Importes.Total : 0));
    return {
        todas: importeTodas,
        entregadas: importeEntregadas
    };
}

/**Obtiene la lista de guias que se va a mostrar en una entrega, dada la clave de la entrega y el state actual */
export function obtenerGuiasDeState(state: models.ViajeAppIniciado, claveEntrega: string): GuiaEntrega[] {
    //Obtemos esta entrega y las otras entregas de la misma direccion
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    //Las guias que se van a mostrar son todas las guias de ese destino, menos las guias que ya fueron incluidas en otras entregas de ese mismo destino
    const guias = obtenerGuiasEntrega(state, claveEntrega);

    type GuiaEntregaDatos = {
        idGuia: number;
        entregada: boolean;
        motivo: string
    };

    //Datos de las guias segun la entrega actual, incluye todas las guias que ya fueron entregadas o rechazadas;
    const guiasEntregaDatos: GuiaEntregaDatos[] = [
        ...entrega.GuiasNoEntregadas.map<GuiaEntregaDatos>(x => ({
            entregada: false,
            idGuia: x.IdGuia,
            motivo: x.Motivo
        })),
        ...entrega.IdGuiasEntregadas.map<GuiaEntregaDatos>(x => ({
            entregada: true,
            idGuia: x,
            motivo: ""
        }))
    ]

    //Por cada guia incluida en esta entrega tenemos que obtener el motivo y si esta seleccionada o no.
    const guiasEntrega = leftJoin(guias, guiasEntregaDatos, (guia, datos) => guia.IdRegistro == datos.idGuia).map<GuiaEntrega>(x => ({
        guia: x.left,
        seleccionada: coalesce(nullsafe(x.right, x => x.entregada), null),
        motivoRechazo: nullsafe(x.right, x => x.motivo) || "",
    }));

    return guiasEntrega;
}



/**Obtiene la información de la cabecera del listado de la entrega */
export function obtenerInfoEntrega(state: models.ViajeAppIniciado, claveEntrega: string): InfoEntrega {
    //Obtenemos los datos de las cabeceras
    const entrega = state.model.EntregasRealizadas.filter(x => x.ClaveUnica == claveEntrega)[0];
    const clientes = state.datos.Clientes;
    const direcciones = mapMany(clientes, x => x.Direcciones);
    const idDireccionDestino = entrega.IdDireccionDestino;
    const direccion = first(direcciones, x => x.IdRegistro == idDireccionDestino)!;
    const cliente = first(clientes, x => x.IdRegistro == direccion.IdCliente)!;

    //Obtenemos todos los ids de las guias que van dirigidos al destino de esta entrega
    const idGuiasDestino = state.datos.Guias.filter(x => x.IdDireccionClienteDestino == idDireccionDestino).map(x => x.IdRegistro);
    const cantGuias = idGuiasDestino.length;

    //Obtenemos todas las guias ya entregadas que van dirigidas a ese destino
    const guiasEntregadas = intersect(idGuiasDestino, mapMany(state.model.EntregasRealizadas, x => x.IdGuiasEntregadas));
    //Tambien las guias rechazadas:
    const guiasRechazadas = intersect(idGuiasDestino, mapMany(state.model.EntregasRealizadas, x => x.GuiasNoEntregadas).map(x => x.IdGuia));
    const cantGuiasExcluidas = cantGuias - (guiasEntregadas.length + guiasRechazadas.length);

    return {
        CantGuias: cantGuias,
        GuiasEntregadas: guiasEntregadas.length,
        GuiasExcluidas: cantGuiasExcluidas,
        GuiasRechazadas: guiasRechazadas.length,
        NombreCliente: cliente.Nombre,
        NombreDestino: direccion.Direccion.Descripcion,
        Finalizado: entrega.Finalizado,
        Firma: entrega.Firma,
        AcusesEntrega: obtenerRecordatoriosAcuse(state, claveEntrega)
    };
}

/**Devuelve un nuevo state al aplicar el cambio de seleccionar una guia */
export function onCambiarEntregaRealizada(state: models.ViajeAppIniciado, claveEntrega: string, value: models.EntregaRealizadaViajeAppModel): models.ViajeAppIniciado {
    return onTransformEntregaRealizada(state, claveEntrega, () => value);
}

/**Devuelve un nuevo state al aplicar el cambio de seleccionar una guia */
export function onTransformEntregaRealizada(state: models.ViajeAppIniciado, claveEntrega: string, trans: (x: models.EntregaRealizadaViajeAppModel) => models.EntregaRealizadaViajeAppModel): models.ViajeAppIniciado {
    return {
        ...state,
        model: {
            ...state.model,
            EntregasRealizadas: state.model.EntregasRealizadas.map(x => x.ClaveUnica == claveEntrega ? trans(x) : x)
        }
    };
}

/**Obtiene el tipo de pago de una entrega a domicilio */
export function obtenerTipoPago(state: models.ViajeAppIniciado, claveEntrega: string) {
    const entrega = first(state.model.EntregasRealizadas, x => x.ClaveUnica == claveEntrega);
    return nullsafe(entrega, x => x.TipoPago);
}

/**Borra una entrega realizada */
export function onBorrarEntrega(state: models.ViajeAppIniciado, claveEntrega: string): models.ViajeAppIniciado {
    return {
        ...state,
        model: {
            ...state.model,
            EntregasRealizadas: state.model.EntregasRealizadas.filter(x => x.ClaveUnica != claveEntrega)
        }
    }
}


/**Borra todas las entregas incompletas de una dirección */
export function onBorrarIncompleta(state: models.ViajeAppIniciado, idDireccioncliente: number): models.ViajeAppIniciado {
    return {
        ...state,
        model: {
            ...state.model,
            EntregasIncompletas: state.model.EntregasIncompletas.filter(x => x.IdDireccionDestino != idDireccioncliente)
        }
    }
}

/**Marca como finalizada una entrega */
export function onFinalizarEntrega(state: models.ViajeAppIniciado, claveEntrega: string, firma: models.FirmaModel): models.ViajeAppIniciado {
    const entrega = obtenerEntrega(state, claveEntrega);
    const idDireccion = entrega.IdDireccionDestino;

    const entregaModificada = onTransformEntregaRealizada(state, claveEntrega, x => ({
        ...x,
        Finalizado: true,
        Firma: firma,
        SaldoContabilizadoServidor: false
    }));

    const borrarIncompletas = onBorrarIncompleta(entregaModificada, idDireccion);
    return borrarIncompletas;
};


/**Devuelve un nuevo state al aplicar el cambio de seleccionar una guia ya sea para entrega, rechazo o excluida */
export function onSeleccionarGuiaEntregaChange(entrega: models.EntregaRealizadaViajeAppModel, idGuia: number, value: boolean | null): models.EntregaRealizadaViajeAppModel {
    //Si value es null, se excluye tanto de las no entregas como de las entregadas,
    //Si value es false, se excluye de las entregadas pero se incluye en las no entregadas
    //Si value es true, se excluye de las no entregadas pero se incluye en las entregadas
    const newValue: models.EntregaRealizadaViajeAppModel = {
        ...entrega,
        GuiasNoEntregadas:
            value == false ?
                unionKey([{ IdGuia: idGuia, Motivo: "" }], entrega.GuiasNoEntregadas, x => x.IdGuia) :
                entrega.GuiasNoEntregadas.filter(x => x.IdGuia != idGuia),

        IdGuiasEntregadas:
            value == true ?
                union(entrega.IdGuiasEntregadas, [idGuia]) :
                entrega.IdGuiasEntregadas.filter(x => x != idGuia)
    };
    return newValue;
}

export function onMotivoGuiaEntregaChange(entrega: models.EntregaRealizadaViajeAppModel, idGuia: number, value: string): models.EntregaRealizadaViajeAppModel {
    const newValue: models.EntregaRealizadaViajeAppModel = {
        ...entrega,
        GuiasNoEntregadas: entrega.GuiasNoEntregadas.map(x => x.IdGuia == idGuia ? { ...x, Motivo: value } : x)
    };
    return newValue;
}

export interface SaldoCliente {
    saldo: number;
    limite: number;
    creditoBloqueado: boolean;
}

/**Obtiene el saldo de crédito del cliente, tomando en cuenta el saldo del servidor y el saldo local segun la clave de una entrega dirigida a ese cliente*/
export function obtenerSaldoClienteDeEntrega(state: models.ViajeAppIniciado, claveEntrega: string): SaldoCliente {
    const entrega = obtenerEntrega(state, claveEntrega);
    return obtenerSaldoClienteDeDireccion(state, entrega.IdDireccionDestino);
}

/**Obtiene el saldo de crédito del cliente, tomando en cuenta el saldo del servidor y el saldo local segun el idDireccion de ese cliente*/
export function obtenerSaldoClienteDeDireccion(state: models.ViajeAppIniciado, idDireccionCliente: number): SaldoCliente {
    const cliente = first(state.datos.Clientes, x => any(x.Direcciones, x => x.IdRegistro == idDireccionCliente))!;
    const idCliente = cliente.IdRegistro;
    return obtenerSaldoCliente(state, idCliente);
}

/**Obtiene el saldo de crédito del cliente, tomando en cuenta el saldo del servidor y el saldo local*/
export function obtenerSaldoCliente(state: models.ViajeAppIniciado, idCliente: number): SaldoCliente {
    const cliente = first(state.datos.Clientes, x => x.IdRegistro == idCliente)!;
    const saldoClienteServer = cliente.SaldoCredito;
    const idDireccionesCliente = cliente.Direcciones.map(x => x.IdRegistro);

    const todasGuias = state.datos.Guias;
    //Todas las entregas de ese cliente
    const entregasCliente = state.model.EntregasRealizadas.filter(x => contains(idDireccionesCliente, x.IdDireccionDestino));
    //Todas las entregas a credito de ese cliente:
    const entregasCreditoLocal = entregasCliente.filter(x => x.TipoPago == models.TipoPagoEntregaDomicilio.Credito && !x.SaldoContabilizadoServidor);
    //Ids de las guias que fueron entregadas y pagadas a credito, note que aún falta por filtrar las que 
    const idGuiasEntregadasCredito = mapMany(entregasCreditoLocal, x => x.IdGuiasEntregadas);
    //Guias que fueron entregadas a credito, ahora si filtradas por las que se deben de pagar
    const guiasEntregadasCredito = innerJoin(idGuiasEntregadasCredito, todasGuias, (id, guia) => id == guia.IdRegistro).map(x => x.right).filter(x => x.FletePorCobrar);

    //Sumamos los importes de las guias pagadas a credito que aún no estan contempladas en el servidor
    //Este valor es la diferencia entre el saldo total del ciente y el saldo que el servidor esta reportando
    const totalGuiasEntregadasCredito = sum(guiasEntregadasCredito.map(x => x.Importes ? x.Importes.Total : 0));

    //El saldo del cliente es el saldo del server + saldo 
    const saldoLocal = saldoClienteServer + totalGuiasEntregadasCredito;
    return { saldo: saldoLocal, limite: cliente.DatosCredito.LimiteCredito || 0, creditoBloqueado: cliente.DatosCredito.CreditoBloqueado };
}


// WEBPACK FOOTER //
// ./app/features/general/operativa/items/viajeapp/entrega/logic.tsx